const chapter = {
  id: 'cell-cycle-division',
  slug: 'cell-cycle-division',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Cell Cycle and Cell Division',
        hindi: 'कोशिका चक्र और कोशिका विभाजन',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2-3 questions in NEET',
        hindi: 'NEET में 2-3 प्रश्न',
  },
  syllabusTags: ['Cell Biology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'cell-cycle',
      title: {
        english: 'Cell Cycle',
        hindi: 'कोशिका चक्र',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Sequence of events from one cell division to the next. Human cell cycle ≈ **24 hours**; yeast ≈ 90 min.',
        hindi: 'एक कोशिका विभाजन से दूसरे कोशिका विभाजन तक की घटनाओं का क्रम। मानव कोशिका चक्र ≈ **24 घंटे**; ख़मीर ≈ 90 मिनट. **इंटरफेज़** (~चक्र का 95%) - प्रारंभिक; कोशिका बढ़ती है, डीएनए प्रतिकृति बनाता है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Interphase** (~95 % of cycle) — preparatory; cell grows, DNA replicates.',
        hindi: '**इंटरफ़ेज़** (~चक्र का 95%) - प्रारंभिक; कोशिका बढ़ती है, डीएनए प्रतिकृति बनाता है।',
            },
            {
              english: '• **G₁ phase** — metabolically active; cell grows; no DNA synthesis.',
        hindi: '• **G₁ चरण** - चयापचय रूप से सक्रिय; कोशिका बढ़ती है; कोई डीएनए संश्लेषण नहीं.',
            },
            {
              english: '• **S phase** — DNA replication; chromosomes become 2-chromatid; centriole also duplicates. DNA doubles (2C → 4C); chromosome **number remains the same**.',
        hindi: '• **एस चरण** — डीएनए प्रतिकृति; गुणसूत्र 2-क्रोमैटिड बन जाते हैं; सेंट्रीओल भी नकल करता है। डीएनए दोगुना हो जाता है (2C → 4C); गुणसूत्र **संख्या वही रहती है**।',
            },
            {
              english: '• **G₂ phase** — protein synthesis (esp. tubulin), preparation for mitosis.',
        hindi: '• **जी₂ चरण** - प्रोटीन संश्लेषण (विशेष रूप से ट्यूबुलिन), माइटोसिस की तैयारी।',
            },
            {
              english: '**M phase (Mitosis)** — actual division; ~1 hour.',
        hindi: '**एम चरण (माइटोसिस)** - वास्तविक विभाजन; ~1 घंटा.',
            },
            {
              english: '**G₀ (quiescent) phase** — cells exit cycle; metabolically active but non-dividing (e.g. neurons, cardiac muscle).',
        hindi: '**G₀ (शांत) चरण** - कोशिकाओं का निकास चक्र; चयापचय रूप से सक्रिय लेकिन गैर-विभाजित (जैसे न्यूरॉन्स, हृदय की मांसपेशी)।',
            },
          ],
        },
        {
          type: 'figure',
          svg: '<svg viewBox="0 0 320 200" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Cell cycle pie"><circle cx="160" cy="100" r="70" fill="none" stroke="#222" stroke-width="2"/><path d="M160 100 L160 30 A70 70 0 0 1 220 70 Z" fill="#cfe8ff"/><path d="M160 100 L220 70 A70 70 0 0 1 230 100 Z" fill="#ffe1c4"/><path d="M160 100 L230 100 A70 70 0 0 1 200 160 Z" fill="#d4f5d4"/><path d="M160 100 L200 160 A70 70 0 1 1 160 30 Z" fill="#fff7c0"/><text x="180" y="55" font-size="11">G1</text><text x="218" y="92" font-size="11">S</text><text x="200" y="140" font-size="11">G2</text><text x="120" y="120" font-size="11">M</text></svg>',
          caption: {
            english: 'Cell-cycle wedges: G₁ (largest), S, G₂, M.',
        hindi: 'सेल-साइकिल वेजेज: G₁ (सबसे बड़ा), S, G₂, M.',
          },
        },
      ],
    },
    {
      id: 'mitosis',
      title: {
        english: 'Mitosis (Equational Division)',
        hindi: 'समसूत्री विभाजन (समीकरण विभाजन)',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Single nuclear division producing **two identical daughter cells**, each with same chromosome number as parent. Occurs in somatic cells. Karyokinesis followed by cytokinesis.',
        hindi: 'एकल परमाणु विभाजन **दो समान बेटी कोशिकाओं** का निर्माण करता है, जिनमें से प्रत्येक में माता-पिता के समान गुणसूत्र संख्या होती है। दैहिक कोशिकाओं में होता है। कैरियोकिनेसिस के बाद साइटोकाइनेसिस होता है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Prophase** — chromatin condenses into visible chromosomes (each = 2 sister chromatids); centrosomes move to opposite poles; spindle assembly begins; nuclear envelope & nucleolus disappear (late prophase).',
        hindi: '**प्रोफ़ेज़** - क्रोमैटिन दृश्यमान गुणसूत्रों में संघनित होता है (each = 2 sister chromatids); सेंट्रोसोम विपरीत ध्रुवों में चले जाते हैं; स्पिंडल असेंबली शुरू हो जाती है; परमाणु आवरण और न्यूक्लियोलस गायब हो जाते हैं (देर से प्रोफ़ेज़)।',
            },
            {
              english: '**Metaphase** — chromosomes align at **equatorial plate (metaphase plate)**; spindle fibres attach to **kinetochores** at centromeres. Chromosomes most condensed → studied for karyotype.',
        hindi: '**मेटाफ़ेज़** — गुणसूत्र **भूमध्यरेखीय प्लेट (मेटाफ़ेज़ प्लेट)** पर संरेखित होते हैं; स्पिंडल फाइबर सेंट्रोमियर पर **किनेटोकोर्स** से जुड़ते हैं। गुणसूत्र सर्वाधिक संघनित → कैरियोटाइप के लिए अध्ययन किया गया।',
            },
            {
              english: '**Anaphase** — centromeres split; sister chromatids separate and move to opposite poles (chromatids now called daughter chromosomes).',
        hindi: '**एनाफ़ेज़** - सेंट्रोमियर विभाजित; बहन क्रोमैटिड अलग हो जाते हैं और विपरीत ध्रुवों पर चले जाते हैं (क्रोमैटिड को अब बेटी क्रोमोसोम कहा जाता है)।',
            },
            {
              english: '**Telophase** — chromosomes decondense; nuclear envelope reforms around each set; nucleolus reappears.',
        hindi: '**टेलोफ़ेज़** - गुणसूत्र डिकॉन्डेंस; प्रत्येक सेट के चारों ओर परमाणु आवरण सुधार; न्यूक्लियोलस पुनः प्रकट होता है।',
            },
            {
              english: '**Cytokinesis** — division of cytoplasm. Animal cell: **cleavage furrow** by actin-myosin ring. Plant cell: **cell plate** forms from middle (centrifugal growth) due to rigid cell wall.',
        hindi: '**साइटोकाइनेसिस** - साइटोप्लाज्म का विभाजन। पशु कोशिका: एक्टिन-मायोसिन रिंग द्वारा **क्लीवेज फ़रो**। पादप कोशिका: **कोशिका प्लेट** कठोर कोशिका भित्ति के कारण मध्य (केन्द्रापसारक वृद्धि) से बनती है।',
            },
            {
              english: 'Significance: growth, repair, replacement of cells; maintains chromosome number; restores nucleo-cytoplasmic ratio.',
        hindi: 'महत्व: कोशिकाओं की वृद्धि, मरम्मत, प्रतिस्थापन; गुणसूत्र संख्या बनाए रखता है; न्यूक्लियो-साइटोप्लाज्मिक अनुपात को पुनर्स्थापित करता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'meiosis',
      title: {
        english: 'Meiosis (Reductional Division)',
        hindi: 'अर्धसूत्री विभाजन (न्यूनकारी विभाजन)',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Two successive nuclear divisions (Meiosis I & II) producing **four haploid (n) cells** from one diploid (2n) cell. DNA replicates **only once** (in S of preceding interphase). Site: gamete-forming cells (germ cells).',
        hindi: 'दो क्रमिक परमाणु विभाजन (मियोसिस I और II) एक द्विगुणित (2n) कोशिका से **चार अगुणित (n) कोशिकाओं** का निर्माण करते हैं। डीएनए **केवल एक बार** (पूर्ववर्ती इंटरफेज़ के एस में) दोहराता है। साइट: युग्मक-निर्माण कोशिकाएं (रोगाणु कोशिकाएं)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Meiosis I — reductional** (2n → n).',
        hindi: '**अर्धसूत्रीविभाजन I - न्यूनीकरण** (2n → n)।',
            },
            {
              english: '**Prophase I** (longest, complex) — five sub-stages:',
        hindi: '**प्रोफ़ेज़ I** (सबसे लंबा, जटिल) - पाँच उप-चरण:',
            },
            {
              english: '• **Leptotene** — chromosomes appear as long threads.',
        hindi: '• **लेप्टोटीन** — गुणसूत्र लंबे धागे के रूप में दिखाई देते हैं।',
            },
            {
              english: '• **Zygotene** — homologous pairing (**synapsis**); synaptonemal complex forms; bivalent (tetrad) of 4 chromatids.',
        hindi: '• **जाइगोटीन** — सजातीय युग्मन (**सिनैप्सिस**); सिनैप्टोनेमल जटिल रूप; 4 क्रोमैटिड्स का द्विसंयोजक (टेट्राड)।',
            },
            {
              english: '• **Pachytene** — **crossing over** (exchange of segments) at chiasmata; mediated by recombinase. Major source of genetic variation.',
        hindi: '• **पैचिटीन** - **चियास्माटा पर क्रॉसिंग ओवर** (खंडों का आदान-प्रदान); पुनर्संयोजन द्वारा मध्यस्थता। आनुवंशिक भिन्नता का प्रमुख स्रोत.',
            },
            {
              english: '• **Diplotene** — synaptonemal complex dissolves; homologues separate but remain joined at chiasmata. Long-lasting in oocytes (months/years).',
        hindi: '• **डिप्लोटीन** - सिनैप्टोनेमल कॉम्प्लेक्स घुल जाता है; समजात अलग हो जाते हैं लेकिन चियास्माटा में जुड़े रहते हैं। oocytes में लंबे समय तक रहने वाला (months/years)।',
            },
            {
              english: '• **Diakinesis** — terminalisation of chiasmata; nuclear envelope breaks down.',
        hindi: '• **डायकाइनेसिस** - चियास्माटा का अंतीकरण; परमाणु आवरण टूट जाता है।',
            },
            {
              english: '**Metaphase I** — bivalents align at equator; homologous pairs face opposite poles.',
        hindi: '**मेटाफ़ेज़ I** - द्विसंयोजक भूमध्य रेखा पर संरेखित होते हैं; सजातीय जोड़े विपरीत ध्रुवों का सामना करते हैं।',
            },
            {
              english: '**Anaphase I** — **homologous chromosomes separate** (each still has 2 chromatids). Sister chromatids stay together. Reduction occurs here.',
        hindi: '**अनाफ़ेज़ I** - **समजात गुणसूत्र अलग हो जाते हैं** (प्रत्येक में अभी भी 2 क्रोमैटिड होते हैं)। सिस्टर क्रोमैटिड्स एक साथ रहते हैं। यहां कमी होती है.',
            },
            {
              english: '**Telophase I** — nuclear envelope reforms (sometimes); cytokinesis follows giving 2 haploid cells.',
        hindi: '**टेलोफ़ेज़ I** - परमाणु आवरण सुधार (कभी-कभी); साइटोकाइनेसिस के बाद 2 अगुणित कोशिकाएँ मिलती हैं।',
            },
            {
              english: '**Interkinesis** — short gap; **no S phase** (no DNA replication).',
        hindi: '**इंटरकाइनेसिस** - छोटा अंतराल; **कोई एस चरण नहीं** (कोई डीएनए प्रतिकृति नहीं)।',
            },
            {
              english: '**Meiosis II — equational** — like mitosis but on haploid cells; centromeres divide, sister chromatids separate. Produces 4 haploid cells total.',
        hindi: '**अर्धसूत्रीविभाजन II - समीकरणात्मक** - समसूत्रण की तरह लेकिन अगुणित कोशिकाओं पर; सेंट्रोमियर विभाजित हो जाते हैं, सिस्टर क्रोमैटिड अलग हो जाते हैं। कुल 4 अगुणित कोशिकाओं का निर्माण करता है।',
            },
          ],
        },
        {
          type: 'note',
          text: {
            english: 'Significance: maintains constant chromosome number across generations; introduces variation via crossing over and independent assortment — raw material for evolution.',
        hindi: 'महत्व: पीढ़ियों में निरंतर गुणसूत्र संख्या बनाए रखता है; क्रॉसिंग ओवर और स्वतंत्र वर्गीकरण के माध्यम से विविधता का परिचय देता है - विकास के लिए कच्चा माल।',
          },
        },
      ],
    },
    {
      id: 'comparison',
      title: {
        english: 'Mitosis vs Meiosis — Quick Compare',
        hindi: 'समसूत्री बनाम अर्धसूत्री — शीघ्र तुलना',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Feature', 'Mitosis', 'Meiosis'],
          rows: [
            ['Divisions', 'One', 'Two (I + II)'],
            ['Daughter cells', '2', '4'],
            ['Ploidy', '2n → 2n', '2n → n'],
            ['DNA replication', 'Once before division', 'Once before Meiosis I only'],
            ['Synapsis & crossing over', 'Absent', 'Present (Pachytene)'],
            ['Genetic outcome', 'Identical to parent', 'Genetically variable'],
            ['Site', 'Somatic cells', 'Germ cells (gamete formation)'],
            ['Function', 'Growth, repair', 'Sexual reproduction, variation'],
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Cell cycle: G}_1 \\to S \\to G_2 \\to M',
      desc: {
        english: '',
        hindi: 'कोशिका चक्र: जी 1 एस जी 2 एम',
      },
    },
    {
      latex: '\\text{S phase: 2C} \\to 4C \\text{ (DNA doubles, chromosome number same)}',
      desc: {
        english: '',
        hindi: 'एस चरण: 2सी 4सी (डीएनए दोगुना, गुणसूत्र संख्या समान)',
      },
    },
    {
      latex: '\\text{Mitosis: 2n} \\to 2n \\text{ (2 daughter cells)}',
      desc: {
        english: '',
        hindi: 'मिटोसिस: 2एन 2एन (2 बेटी कोशिकाएं)',
      },
    },
    {
      latex: '\\text{Meiosis: 2n} \\to n \\text{ (4 daughter cells)}',
      desc: {
        english: '',
        hindi: 'अर्धसूत्रीविभाजन: 2n n (4 पुत्री कोशिकाएँ)',
      },
    },
    {
      latex: '\\text{Crossing over: pachytene of Prophase I}',
      desc: {
        english: '',
        hindi: 'क्रॉसिंग ओवर: प्रोफ़ेज़ I का पचीटीन',
      },
    },
    {
      latex: '\\text{Cytokinesis: animal = furrow; plant = cell plate}',
      desc: {
        english: '',
        hindi: 'साइटोकाइनेसिस: पशु = नाली; पौधा = कोशिका प्लेट',
      },
    },
    {
      latex: '\\text{Anaphase I: homologues separate; Anaphase II: chromatids separate}',
      desc: {
        english: '',
        hindi: 'एनाफ़ेज़ I: होमोलॉग्स अलग; एनाफ़ेज़ II: क्रोमैटिड अलग हो जाते हैं',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Meiosis',
      problem: {
        english: 'Crossing over occurs during which sub-stage of Prophase I?',
        hindi: 'क्रॉसिंग ओवर प्रोफ़ेज़ I के किस उप-चरण के दौरान होता है?',
      },
      answer: {
        english: 'Pachytene',
        hindi: 'पचीटीन',
      },
      solution: {
        english: 'Synaptonemal complex fully formed; recombination nodules mediate exchange.',
        hindi: 'सिनैप्टोनेमल कॉम्प्लेक्स पूरी तरह से बना; पुनर्संयोजन नोड्यूल विनिमय में मध्यस्थता करते हैं।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Cell cycle',
      problem: {
        english: 'DNA replication occurs in:',
        hindi: 'डीएनए प्रतिकृति होती है:',
      },
      answer: {
        english: 'S phase',
        hindi: 'एस चरण',
      },
      solution: {
        english: 'Synthesis phase of interphase.',
        hindi: 'इंटरफ़ेज़ का संश्लेषण चरण।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Mitosis',
      problem: {
        english: 'Cell plate formation during cytokinesis is characteristic of:',
        hindi: 'साइटोकाइनेसिस के दौरान सेल प्लेट का निर्माण इसकी विशेषता है:',
      },
      answer: {
        english: 'Plant cells',
        hindi: 'पादप कोशिकाएँ',
      },
      solution: {
        english: 'Rigid cell wall prevents furrowing; vesicles fuse at equator.',
        hindi: 'कठोर कोशिका भित्ति खांचे बनने से रोकती है; पुटिकाएँ भूमध्य रेखा पर विलीन हो जाती हैं।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Anaphase',
      problem: {
        english: 'Sister chromatids separate during:',
        hindi: 'सिस्टर क्रोमैटिड इस दौरान अलग हो जाते हैं:',
      },
      answer: {
        english: 'Anaphase of mitosis (and Anaphase II of meiosis)',
        hindi: 'माइटोसिस का एनाफेज (और अर्धसूत्रीविभाजन का एनाफेज II)',
      },
      solution: {
        english: 'Anaphase I separates homologues, not chromatids.',
        hindi: 'एनाफ़ेज़ I समरूपों को अलग करता है, क्रोमैटिड्स को नहीं।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'G0',
      problem: {
        english: 'Cells that exit cell cycle and stop dividing enter:',
        hindi: 'कोशिकाएँ जो कोशिका चक्र से बाहर निकलती हैं और विभाजित होना बंद कर देती हैं, प्रवेश करती हैं:',
      },
      answer: {
        english: 'G₀ (quiescent) phase',
        hindi: 'G₀ (शांत) चरण',
      },
      solution: {
        english: 'Metabolically active but non-dividing, e.g. neurons.',
        hindi: 'चयापचय रूप से सक्रिय लेकिन गैर-विभाजित, जैसे न्यूरॉन्स.',
      },
    },
  ],
  next: {
    slug: 'principles-of-inheritance',
    title: 'Principles of Inheritance and Variation',
  },
};

export default chapter;
